// @ts-nocheck - Temporary fix for backend integration types
import { httpClient } from '@/lib/httpClient';
import { API_CONFIG } from '@/config/api';
import type { ServiceProvider, User } from '@/types';

export class BackendProviderService {
  private getAuthToken(): string | undefined {
    return localStorage.getItem('auth_token') || undefined;
  }

  private getUserId(): string | undefined {
    return localStorage.getItem('user_id') || undefined;
  }

  // Create provider profile
  async createProvider(providerData: {
    userId: string;
    businessName: string;
    city: string;
    county: string;
    services: string[];
    description: string;
  }): Promise<any> {
    const response = await httpClient.post(API_CONFIG.ENDPOINTS.PROVIDERS, {
      userId: providerData.userId,
      businessName: providerData.businessName,
      city: providerData.city,
      county: providerData.county,
      services: providerData.services,
      description: providerData.description
    }, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to create provider profile');
    }

    return response.data;
  }

  // Get all providers
  async getProviders(filters?: {
    city?: string;
    county?: string;
    service?: string;
    verified?: boolean;
    available?: boolean;
  }): Promise<ServiceProvider[]> {
    const params: Record<string, string> = {};

    if (filters?.city) params.city = filters.city;
    if (filters?.county) params.county = filters.county;
    if (filters?.service) params.service = filters.service;
    if (filters?.verified !== undefined) params.verified = filters.verified.toString();
    if (filters?.available !== undefined) params.available = filters.available.toString();

    const response = await httpClient.get(API_CONFIG.ENDPOINTS.PROVIDERS, {
      params
      // No token needed - this is a public endpoint
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch providers');
    }

    return (response.data || []).map((provider: any) => this.mapProviderFromBackend(provider));
  }

  // Get provider by ID
  async getProviderById(id: string): Promise<ServiceProvider | null> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${id}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      if (response.error?.includes('not found')) return null;
      throw new Error(response.error || 'Failed to fetch provider');
    }

    return this.mapProviderFromBackend(response.data);
  }

  // Update provider
  async updateProvider(id: string, updates: Partial<{
    businessName: string;
    description: string;
    city: string;
    county: string;
    services: string[];
    availableNow: boolean;
  }>): Promise<ServiceProvider> {
    const response = await httpClient.put(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${id}`, updates, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update provider');
    }

    return this.mapProviderFromBackend(response.data);
  }

  // Search providers
  async searchProviders(query: string, filters?: {
    location?: string;
    service?: string;
    maxDistance?: number;
  }): Promise<ServiceProvider[]> {
    const params: Record<string, string> = { q: query };

    if (filters?.location) params.location = filters.location;
    if (filters?.service) params.service = filters.service;
    if (filters?.maxDistance) params.max_distance = filters.maxDistance.toString();

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.PROVIDERS}/search`, {
      params
      // No token needed - this is a public endpoint
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to search providers');
    }

    return (response.data || []).map((provider: any) => this.mapProviderFromBackend(provider));
  }

  // Provider Analytics
  async getProviderAnalytics(providerId: string, period?: string): Promise<any> {
    const params: Record<string, string> = {};
    if (period) params.period = period;

    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.ANALYTICS}/provider/${providerId}`, {
      params,
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch analytics');
    }

    return response.data;
  }

  // Provider Dashboard Data
  async getProviderDashboard(providerId: string): Promise<any> {
    const response = await httpClient.get(`/api/v1/auth/providers/${providerId}/dashboard`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch dashboard data');
    }

    return response.data;
  }

  // Team Management
  async getTeamMembers(providerId: string): Promise<any[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/team`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch team members');
    }

    return response.data || [];
  }

  async addTeamMember(providerId: string, memberData: {
    name: string;
    email: string;
    role: string;
    permissions: string[];
  }): Promise<any> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/team`, memberData, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to add team member');
    }

    return response.data;
  }

  async updateMemberStatus(providerId: string, memberId: string, status: string): Promise<void> {
    const response = await httpClient.patch(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/team/${memberId}/status`, {
      status
    }, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update member status');
    }
  }

  async removeMember(providerId: string, memberId: string): Promise<void> {
    const response = await httpClient.delete(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/team/${memberId}`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to remove team member');
    }
  }

  // Availability Management
  async getAvailability(providerId: string): Promise<any> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/availability`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch availability');
    }

    return response.data;
  }

  async updateAvailability(providerId: string, availability: any): Promise<void> {
    const response = await httpClient.put(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/availability`, availability, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to update availability');
    }
  }

  async toggleAvailability(providerId: string): Promise<void> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/availability/toggle`, {}, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to toggle availability');
    }
  }

  // Reviews
  async getProviderReviews(providerId: string, page = 1, limit = 10): Promise<any> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/reviews`, {
      params: { page: page.toString(), limit: limit.toString() },
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch reviews');
    }

    return response.data;
  }

  async getReviewStats(providerId: string): Promise<any> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/reviews/stats`, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch review stats');
    }

    return response.data;
  }

  async respondToReview(providerId: string, reviewId: string, response: string): Promise<void> {
    const result = await httpClient.post(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/reviews/${reviewId}/response`, {
      response
    }, {
      token: this.getAuthToken()
    });

    if (!result.success) {
      throw new Error(result.error || 'Failed to respond to review');
    }
  }

  // Calendar Events
  async getCalendarEvents(providerId: string, startDate: string, endDate: string): Promise<any[]> {
    const response = await httpClient.get(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/calendar/events`, {
      params: { start_date: startDate, end_date: endDate },
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch calendar events');
    }

    return response.data || [];
  }

  async createCalendarEvent(providerId: string, eventData: any): Promise<any> {
    const response = await httpClient.post(`${API_CONFIG.ENDPOINTS.PROVIDERS}/${providerId}/calendar/events`, eventData, {
      token: this.getAuthToken()
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to create calendar event');
    }

    return response.data;
  }

  // Update calendar event (NEW - connects to PUT /api/v1/auth/providers/{id}/calendar/events/{eventId})
  async updateCalendarEvent(providerId: string, eventId: string, eventData: any): Promise<any> {
    console.log('🔄 Updating calendar event:', { providerId, eventId, eventData });

    const response = await httpClient.put(
      `/api/v1/auth/providers/${providerId}/calendar/events/${eventId}`,
      eventData,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Calendar event update failed:', response.error);
      throw new Error(response.error || 'Failed to update calendar event');
    }

    console.log('✅ Calendar event updated successfully:', response.data);
    return response.data;
  }

  // Delete calendar event (NEW - connects to DELETE /api/v1/auth/providers/{id}/calendar/events/{eventId})
  async deleteCalendarEvent(providerId: string, eventId: string): Promise<void> {
    console.log('🔄 Deleting calendar event:', { providerId, eventId });

    const response = await httpClient.delete(
      `/api/v1/auth/providers/${providerId}/calendar/events/${eventId}`,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Calendar event deletion failed:', response.error);
      throw new Error(response.error || 'Failed to delete calendar event');
    }

    console.log('✅ Calendar event deleted successfully');
  }

  // Get weekly calendar view (NEW - connects to GET /api/v1/auth/providers/{id}/calendar/week)
  async getWeeklyCalendar(providerId: string, weekStart: string): Promise<any> {
    console.log('🔄 Getting weekly calendar:', { providerId, weekStart });

    const response = await httpClient.get(
      `/api/v1/auth/providers/${providerId}/calendar/week`,
      {
        params: { week_start: weekStart },
        token: this.getAuthToken()
      }
    );

    if (!response.success) {
      console.error('❌ Weekly calendar fetch failed:', response.error);
      throw new Error(response.error || 'Failed to fetch weekly calendar');
    }

    console.log('✅ Weekly calendar fetched successfully');
    return response.data;
  }

  // Update availability schedule (NEW - connects to PUT /api/v1/auth/providers/{id}/availability/schedule)
  async updateAvailabilitySchedule(providerId: string, schedule: any): Promise<void> {
    console.log('🔄 Updating availability schedule:', { providerId });

    const response = await httpClient.put(
      `/api/v1/auth/providers/${providerId}/availability/schedule`,
      schedule,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Availability schedule update failed:', response.error);
      throw new Error(response.error || 'Failed to update availability schedule');
    }

    console.log('✅ Availability schedule updated successfully');
  }

  // Get referrals (NEW - connects to GET /api/v1/auth/providers/{id}/referrals)
  async getReferrals(providerId: string): Promise<any[]> {
    console.log('🔄 Getting referrals:', { providerId });

    const response = await httpClient.get(
      `/api/v1/auth/providers/${providerId}/referrals`,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Referrals fetch failed:', response.error);
      throw new Error(response.error || 'Failed to fetch referrals');
    }

    console.log('✅ Referrals fetched successfully');
    return response.data || [];
  }

  // Get offers (NEW - connects to GET /api/v1/auth/providers/{id}/offers)
  async getOffers(providerId: string): Promise<any[]> {
    console.log('🔄 Getting offers:', { providerId });

    const response = await httpClient.get(
      `/api/v1/auth/providers/${providerId}/offers`,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Offers fetch failed:', response.error);
      throw new Error(response.error || 'Failed to fetch offers');
    }

    console.log('✅ Offers fetched successfully');
    return response.data || [];
  }

  // Create offer (NEW - connects to POST /api/v1/auth/providers/{id}/offers)
  async createOffer(providerId: string, offerData: {
    title: string;
    description: string;
    discount_percent?: number;
    discount_amount?: number;
    valid_from: string;
    valid_until: string;
    terms?: string;
  }): Promise<any> {
    console.log('🔄 Creating offer:', { providerId, offerData });

    const response = await httpClient.post(
      `/api/v1/auth/providers/${providerId}/offers`,
      offerData,
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Offer creation failed:', response.error);
      throw new Error(response.error || 'Failed to create offer');
    }

    console.log('✅ Offer created successfully');
    return response.data;
  }

  // Toggle offer status (NEW - connects to PATCH /api/v1/auth/providers/{id}/offers/{offerId}/toggle)
  async toggleOffer(providerId: string, offerId: string): Promise<void> {
    console.log('🔄 Toggling offer:', { providerId, offerId });

    const response = await httpClient.patch(
      `/api/v1/auth/providers/${providerId}/offers/${offerId}/toggle`,
      {},
      { token: this.getAuthToken() }
    );

    if (!response.success) {
      console.error('❌ Offer toggle failed:', response.error);
      throw new Error(response.error || 'Failed to toggle offer');
    }

    console.log('✅ Offer toggled successfully');
  }

  // Get marketing metrics (NEW - connects to GET /api/v1/auth/providers/{id}/marketing/metrics)
  async getMarketingMetrics(providerId: string, period?: string): Promise<any> {
    console.log('🔄 Getting marketing metrics:', { providerId, period });

    const params: Record<string, string> = {};
    if (period) params.period = period;

    const response = await httpClient.get(
      `/api/v1/auth/providers/${providerId}/marketing/metrics`,
      {
        params,
        token: this.getAuthToken()
      }
    );

    if (!response.success) {
      console.error('❌ Marketing metrics fetch failed:', response.error);
      throw new Error(response.error || 'Failed to fetch marketing metrics');
    }

    console.log('✅ Marketing metrics fetched successfully');
    return response.data;
  }

  // Media Upload
  async uploadMedia(file: File, metadata?: any): Promise<any> {
    const response = await httpClient.uploadFile(API_CONFIG.ENDPOINTS.MEDIA, file, {
      token: this.getAuthToken(),
      additionalData: metadata
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to upload media');
    }

    return response.data;
  }

  private mapProviderFromBackend(data: any): ServiceProvider {
    // Backend returns data in profile_data (JSONB) and some top-level fields
    // Extract from profile_data first, then fall back to top-level fields
    const profileData = data.profile_data || data.profileData || {};
    
    // Get business name from top-level (extracted by backend) or profile_data
    const businessName = data.business_name || profileData.businessName || profileData.business_name || 'Provider';
    
    // Get city and county from profile_data or top-level
    const city = profileData.city || data.city || '';
    const county = profileData.county || data.county || '';
    const location = city && county ? `${city}, ${county}` : (data.location || '');
    
    // Get verified status (backend returns is_verified)
    const verified = data.is_verified !== undefined ? data.is_verified : (data.verified || profileData.verified || false);
    
    // Get bio/description from profile_data or top-level
    const bio = profileData.bio || profileData.description || data.description || data.bio || '';
    
    // Get rating and review count from profile_data or top-level
    const rating = profileData.rating || data.rating || 0;
    const reviewCount = profileData.reviewCount || profileData.review_count || data.review_count || data.reviewCount || 0;
    
    // Get services/specializations from profile_data or top-level
    const services = profileData.services || profileData.specializations || data.services || data.serviceCategories || [];
    
    // Get provider type from profile_data or top-level
    const providerType = profileData.providerType || profileData.provider_type || data.provider_type || data.providerType || 'INDIVIDUAL';
    
    // Get insurance info from profile_data
    const insurance = profileData.insurance || {};
    const hasInsurance = insurance.hasInsurance !== undefined ? insurance.hasInsurance : (insurance.has_insurance || data.has_insurance || false);
    
    // Get certifications from profile_data or top-level
    const certifications = profileData.certifications || data.certifications || [];
    
    // Get years of experience from profile_data or top-level
    const yearsOfExperience = profileData.yearsOfExperience || profileData.years_of_experience || data.years_of_experience || data.yearsOfExperience || 0;
    
    // Get profile image from profile_data or top-level
    const profileImage = profileData.profileImage || profileData.profile_image || data.profile_image || data.profileImage;
    
    // Get pricing from profile_data or top-level
    const pricing = profileData.pricing || {};
    const hourlyRate = pricing.hourlyRate || pricing.hourly_rate || data.hourly_rate || 0;
    const calloutFee = pricing.calloutFee || pricing.callout_fee || data.callout_fee || 0;
    
    // Get availability from profile_data or use defaults
    const availability = profileData.availability || {};
    
    // Get completed jobs from profile_data or top-level
    const completedJobs = profileData.completedJobs || profileData.completed_jobs || data.completed_jobs || data.completedJobs || 0;
    
    // Get response time from profile_data or top-level
    const responseTime = profileData.responseTime || profileData.response_time || data.response_time || data.responseTime || 'Within 2 hours';
    
    // Get languages from profile_data or default
    const languages = profileData.languages || data.languages || ['English'];
    
    // Get background check from profile_data or top-level
    const backgroundCheck = profileData.backgroundCheck || profileData.background_check || data.background_check || data.backgroundCheck || false;

    const employmentHistory = Array.isArray(profileData.employmentHistory) ? profileData.employmentHistory : [];
    
    return {
      id: data.id || data.user_id || data.userId,
      userId: data.user_id || data.userId || data.id,
      businessName,
      bio,
      verified,
      availableNow: profileData.availableNow || profileData.available_now || data.available_now || data.availableNow || false,
      location,
      rating,
      reviewCount,
      pricing: {
        hourlyRate,
        calloutFee
      },
      serviceCategories: services,
      profileImage,
      providerType,
      isPremium: profileData.isPremium || profileData.is_premium || data.is_premium || data.isPremium || false,
      premiumExpiryDate: profileData.premiumExpiryDate || profileData.premium_expiry_date || data.premium_expiry_date ? new Date(profileData.premiumExpiryDate || profileData.premium_expiry_date || data.premium_expiry_date) : undefined,
      subscriptionTier: profileData.subscriptionTier || profileData.subscription_tier || data.subscription_tier || data.subscriptionTier || 'BASIC',
      insurance: {
        hasInsurance,
        provider: insurance.provider || insurance.insurance_provider || data.insurance_provider,
        coverage: insurance.coverage || insurance.insurance_coverage || data.insurance_coverage,
        expiryDate: insurance.expiryDate || insurance.expiry_date || data.insurance_expiry ? new Date(insurance.expiryDate || insurance.expiry_date || data.insurance_expiry) : undefined
      },
      certifications,
      portfolio: profileData.portfolio || data.portfolio || [],
      yearsOfExperience,
      availability: {
        nextAvailable: availability.nextAvailable || availability.next_available ? new Date(availability.nextAvailable || availability.next_available) : new Date(),
        workingHours: availability.workingHours || availability.working_hours || {
          monday: { start: '08:00', end: '18:00', available: true },
          tuesday: { start: '08:00', end: '18:00', available: true },
          wednesday: { start: '08:00', end: '18:00', available: true },
          thursday: { start: '08:00', end: '18:00', available: true },
          friday: { start: '08:00', end: '18:00', available: true },
          saturday: { start: '09:00', end: '16:00', available: false },
          sunday: { start: '10:00', end: '14:00', available: false }
        },
        emergencyService: availability.emergencyService || availability.emergency_service || data.emergency_service || false,
        weekendService: availability.weekendService || availability.weekend_service || data.weekend_service || false
      },
      completedJobs,
      responseTime,
      languages,
      backgroundCheck,
      employmentHistory,
      createdAt: data.created_at ? new Date(data.created_at) : new Date(),
      updatedAt: data.updated_at ? new Date(data.updated_at) : new Date()
    };
  }
}

export const backendProviderService = new BackendProviderService();